"use client";

import React, { useState, useEffect } from "react";
import {
  X,
  Sliders,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
  RotateCcw,
  Eye,
  EyeOff,
  Sparkles,
  Layers,
  ArrowRight,
} from "lucide-react";
import {
  WorkspaceConfig,
  WorkspaceFieldConfig,
  WorkspacePipelineStage,
  WORKSPACE_PRESETS,
} from "@/lib/workspacePresets";

interface WorkspaceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: WorkspaceConfig;
  onSave: (newConfig: WorkspaceConfig) => void;
  isPlatformAdmin?: boolean;
}

export default function WorkspaceSettingsModal({
  isOpen,
  onClose,
  config,
  onSave,
  isPlatformAdmin = false,
}: WorkspaceSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<"profile" | "fields" | "stages">(
    isPlatformAdmin ? "profile" : "fields"
  );
  const [profile, setProfile] = useState<string>(config?.businessProfile || "trading");
  const [fields, setFields] = useState<WorkspaceFieldConfig[]>(
    config?.fields ? JSON.parse(JSON.stringify(config.fields)) : []
  );
  const [pipelineStages, setPipelineStages] = useState<WorkspacePipelineStage[]>(
    config?.pipelineStages ? JSON.parse(JSON.stringify(config.pipelineStages)) : []
  );

  const [newStageName, setNewStageName] = useState("");
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState<"text" | "number" | "select" | "textarea">("text");
  const [showAddFieldForm, setShowAddFieldForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Synchronize internal state whenever config prop or modal visibility changes
  useEffect(() => {
    if (config) {
      setProfile(config.businessProfile || "trading");
      setFields(config.fields ? JSON.parse(JSON.stringify(config.fields)) : []);
      setPipelineStages(config.pipelineStages ? JSON.parse(JSON.stringify(config.pipelineStages)) : []);
      setActiveTab(isPlatformAdmin ? "profile" : "fields");
      setStatusMsg(null);
    }
  }, [config, isOpen, isPlatformAdmin]);

  if (!isOpen) return null;

  // Handle Preset Switching (Superadmin only)
  const handleApplyPreset = (presetKey: string) => {
    if (!isPlatformAdmin) return;
    const preset = WORKSPACE_PRESETS[presetKey];
    if (!preset) return;
    setProfile(presetKey);
    setFields(JSON.parse(JSON.stringify(preset.fields)));
    setPipelineStages(JSON.parse(JSON.stringify(preset.pipelineStages)));
    setStatusMsg({
      type: "success",
      text: `Applied "${preset.name}" preset! Click "Save Changes" below to confirm.`,
    });
  };

  // Field updates
  const handleFieldLabelChange = (index: number, newLabel: string) => {
    setFields((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], label: newLabel };
      return copy;
    });
  };

  const handleFieldVisibilityToggle = (index: number) => {
    setFields((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], visible: !copy[index].visible };
      return copy;
    });
  };

  const handleAddCustomField = () => {
    if (!newFieldLabel.trim()) return;
    const cleanSlug = "custom_" + newFieldLabel.trim().toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 24);

    if (fields.some((f) => f.key === cleanSlug)) {
      alert("A field with this name already exists.");
      return;
    }

    const newField: WorkspaceFieldConfig = {
      key: cleanSlug,
      label: newFieldLabel.trim(),
      type: newFieldType,
      visible: true,
      required: false,
      placeholder: `Enter ${newFieldLabel.trim()}`,
    };

    setFields((prev) => [...prev, newField]);
    setNewFieldLabel("");
    setShowAddFieldForm(false);
  };

  const handleDeleteField = (key: string) => {
    setFields((prev) => prev.filter((f) => f.key !== key));
  };

  // Stage updates
  const handleStageNameChange = (index: number, newName: string) => {
    setPipelineStages((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], name: newName };
      return copy;
    });
  };

  const handleDeleteStage = (index: number) => {
    if (pipelineStages.length <= 2) {
      alert("A pipeline requires at least 2 stages.");
      return;
    }
    setPipelineStages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddStage = () => {
    if (!newStageName.trim()) return;
    const newStage: WorkspacePipelineStage = {
      id: newStageName.trim(),
      name: newStageName.trim(),
      color: "text-blue-500",
      bg: "bg-blue-500/10",
      border: "border-blue-500/20",
      position: pipelineStages.length + 1,
    };
    setPipelineStages((prev) => [...prev, newStage]);
    setNewStageName("");
  };

  // Save to API
  const handleSave = async () => {
    setIsSaving(true);
    setStatusMsg(null);
    try {
      const payload: Partial<WorkspaceConfig> & { userId?: number } = {
        userId: config?.userId,
        businessProfile: profile,
        fields,
        pipelineStages,
      };

      const res = await fetch("/api/crm/workspace/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to save configuration.");
      }

      onSave(data.config);
      setStatusMsg({ type: "success", text: "Workspace configuration saved successfully!" });
      setTimeout(() => {
        onClose();
      }, 700);
    } catch (err: any) {
      setStatusMsg({ type: "error", text: err.message || "Failed to save configuration." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-3xl max-h-[92vh] flex flex-col bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl overflow-hidden text-slate-100">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center text-primary shadow-inner">
              <Sliders className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-white tracking-tight">
                Workspace Configuration & Customization
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Customize your business profile, table header vocabulary, and pipeline stages
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-3 border-b border-slate-800 flex gap-2 bg-slate-950/40">
          {isPlatformAdmin && (
            <button
              onClick={() => setActiveTab("profile")}
              className={`px-4 py-2.5 rounded-t-xl text-xs font-bold border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === "profile"
                  ? "border-primary text-primary bg-primary/10"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
            >
              <span>🏢 1. Business Profile Presets (Admin Only)</span>
            </button>
          )}
          <button
            onClick={() => setActiveTab("fields")}
            className={`px-4 py-2.5 rounded-t-xl text-xs font-bold border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "fields"
                ? "border-primary text-primary bg-primary/10"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <span>🏷️ {isPlatformAdmin ? "2." : "1."} Fields & Table Headers</span>
          </button>
          <button
            onClick={() => setActiveTab("stages")}
            className={`px-4 py-2.5 rounded-t-xl text-xs font-bold border-b-2 transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "stages"
                ? "border-primary text-primary bg-primary/10"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <span>⚡ {isPlatformAdmin ? "3." : "2."} Pipeline Stages</span>
          </button>
        </div>

        {/* Tenant Active Profile Notice & Upgrade Contact */}
        {!isPlatformAdmin && (
          <div className="mx-6 mt-4 p-3.5 rounded-2xl bg-slate-800/40 border border-slate-700/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-base shrink-0">
                🏢
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-300 font-medium">Assigned Business Profile:</span>
                  <span className="font-bold text-white bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-md text-[11px] capitalize">
                    {WORKSPACE_PRESETS[profile]?.name || profile}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  You can customize fields, column headers, and pipeline stages below. To switch your company profile or add multi-business verticals, contact Johnson Softwares.
                </p>
              </div>
            </div>
            <a
              href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares%2C%20I%20want%20to%20change%20or%20upgrade%20my%20CRM%20business%20profile%20preset"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-semibold text-xs transition-colors shrink-0"
            >
              <span>Contact to Change Profile</span>
            </a>
          </div>
        )}

        {/* Status Toast */}
        {statusMsg && (
          <div
            className={`mx-6 mt-4 p-3 rounded-xl text-xs flex items-center gap-2.5 ${
              statusMsg.type === "success"
                ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
                : "bg-rose-500/10 border border-rose-500/20 text-rose-400"
            }`}
          >
            {statusMsg.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            <span>{statusMsg.text}</span>
          </div>
        )}

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* TAB 1: Business Profile Presets (Superadmin only) */}
          {activeTab === "profile" && isPlatformAdmin && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 text-xs text-slate-300">
                <span className="font-bold text-white block mb-1">
                  How does your business operate?
                </span>
                Selecting a profile adjusts the default terminology, table headers, and sales stages
                without altering the core CRM database. You can still customize any individual field or stage.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {Object.entries(WORKSPACE_PRESETS).map(([key, preset]) => {
                  const isSelected = profile === key;
                  return (
                    <div
                      key={key}
                      onClick={() => handleApplyPreset(key)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer text-left flex flex-col justify-between ${
                        isSelected
                          ? "bg-primary/10 border-primary shadow-lg shadow-primary/10 ring-1 ring-primary"
                          : "bg-slate-800/60 border-slate-700/70 hover:bg-slate-800 hover:border-slate-600"
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <span className="text-2xl">{preset.icon}</span>
                            <span className="font-bold text-sm text-white">{preset.name}</span>
                          </div>
                          {isSelected && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary text-white">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                          {preset.description}
                        </p>
                      </div>

                      <div className="mt-3 pt-2.5 border-t border-slate-700/50 flex items-center justify-between text-[11px] text-slate-400 font-medium">
                        <span>{preset.pipelineStages.length} Pipeline Stages</span>
                        <span className="text-primary hover:underline flex items-center gap-1 font-semibold">
                          Apply Preset <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 2: Fields & Table Headers */}
          {activeTab === "fields" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Rename table column labels, add custom business fields, and customize visibility:
                </p>
                <button
                  type="button"
                  onClick={() => setShowAddFieldForm(!showAddFieldForm)}
                  className="px-3 py-1.5 rounded-xl bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Custom Field</span>
                </button>
              </div>

              {/* Add Custom Field Form */}
              {showAddFieldForm && (
                <div className="p-4 rounded-2xl bg-slate-800/80 border border-primary/40 space-y-3 animate-in fade-in duration-150">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary" /> Create New Business Field
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowAddFieldForm(false)}
                      className="text-slate-400 hover:text-white text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                        Field Label
                      </label>
                      <input
                        type="text"
                        value={newFieldLabel}
                        onChange={(e) => setNewFieldLabel(e.target.value)}
                        placeholder="e.g. GST Number, Delivery Port, Vessel Name"
                        className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs font-medium text-white focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                        Data Type
                      </label>
                      <select
                        value={newFieldType}
                        onChange={(e) => setNewFieldType(e.target.value as any)}
                        className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs font-medium text-white focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
                      >
                        <option value="text">Text / String</option>
                        <option value="number">Number / Metric</option>
                        <option value="textarea">Multi-line Notes</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={handleAddCustomField}
                      disabled={!newFieldLabel.trim()}
                      className="px-4 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer"
                    >
                      Confirm &amp; Add Field
                    </button>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-slate-800 overflow-hidden bg-slate-950/40">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-800/80 text-slate-400 uppercase font-semibold border-b border-slate-800">
                    <tr>
                      <th className="px-4 py-3">Internal Key</th>
                      <th className="px-4 py-3">Display Label (Customizable)</th>
                      <th className="px-4 py-3 text-center">Visible</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {fields.map((field, idx) => {
                      const isCustom = field.key.startsWith("custom_");
                      return (
                        <tr key={field.key} className="hover:bg-slate-800/30">
                          <td className="px-4 py-3 font-mono text-[11px] text-slate-400 flex items-center gap-1.5">
                            <span>{field.key}</span>
                            {isCustom && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/20 text-primary">
                                Custom
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => handleFieldLabelChange(idx, e.target.value)}
                              className="w-full max-w-xs px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs font-semibold text-white focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleFieldVisibilityToggle(idx)}
                              className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                                field.visible
                                  ? "text-emerald-400 hover:bg-emerald-500/10"
                                  : "text-slate-500 hover:bg-slate-800"
                              }`}
                              title={field.visible ? "Visible in UI" : "Hidden from UI"}
                            >
                              {field.visible ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isCustom ? (
                              <button
                                type="button"
                                onClick={() => handleDeleteField(field.key)}
                                className="p-1 rounded text-rose-400 hover:bg-rose-500/20 transition-colors cursor-pointer"
                                title="Delete Custom Field"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <span className="text-slate-600 text-[10px]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: Pipeline Stages */}
          {activeTab === "stages" && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400">
                Customize your commercial pipeline stages. Drag-and-drop or rename columns in Kanban:
              </p>

              <div className="space-y-2.5">
                {pipelineStages.map((stage, idx) => (
                  <div
                    key={stage.id}
                    className="p-3.5 rounded-xl bg-slate-800/60 border border-slate-700 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className="w-6 h-6 rounded-lg bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => handleStageNameChange(idx, e.target.value)}
                        className="flex-1 max-w-xs px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-xs font-bold text-white focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      {stage.isWon && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          Won / Closed
                        </span>
                      )}
                      {stage.isLost && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-500/10 text-slate-400 border border-slate-500/20">
                          Lost / Inactive
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteStage(idx)}
                      className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/10 transition-colors cursor-pointer"
                      title="Delete Stage"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New Stage */}
              <div className="flex gap-2 pt-2">
                <input
                  type="text"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  placeholder="New Stage Title (e.g. Sample Approval)..."
                  className="flex-1 px-3.5 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <button
                  type="button"
                  onClick={handleAddStage}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs font-bold text-white flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Add Stage
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
          {isPlatformAdmin ? (
            <button
              type="button"
              onClick={() => handleApplyPreset("trading")}
              className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1.5 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to Trading Preset
            </button>
          ) : (
            <div className="text-[11px] text-slate-500 font-medium">
              Changes apply strictly to your workspace pipeline.
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-700 hover:bg-slate-800 text-xs font-semibold text-slate-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={handleSave}
              className="px-6 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white text-xs font-bold shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-50 transition-all cursor-pointer"
            >
              {isSaving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Save Configuration</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
